import { pauseConnector, deleteConnector, resumeConnector, updateConnector } from "@/shared/api"
import { DataConnectorsType, StatusEnum } from "@/types/DataConnectorsType"
import { getAuthToken } from "@/utils/auth"
import { produce } from "../utils/immer"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { logs } from "../../shared_lib/logger"

type ConnectorsState = {
  connectors: DataConnectorsType[]
}

type ConnectorsActions = {
  setConnectors: (connectors: DataConnectorsType[]) => void
  deleteConnector: (id: string) => void
  updateStoreConnector: (id: string, updatedData: any) => void
  updateConnectorById: (newData: any, id: string) => void
  addStoreConnector: (newData: DataConnectorsType) => void
  pauseConnectorById: (id: string) => Promise<void>
  resumeConnectorById: (id: string) => Promise<void>
}

function matchesConnectorId(item: DataConnectorsType, id: string): boolean {
  const candidates = [item?.connector?._id, item?.integration?._id, item?._id]
  return candidates.some(value => value != null && String(value) === String(id))
}

function applyConnectorStatus(state: ConnectorsState, id: string, status: StatusEnum) {
  const index = state.connectors.findIndex(item => matchesConnectorId(item, id))
  if (index === -1) return
  state.connectors[index] = {
    ...state.connectors[index],
    connector: { ...state.connectors[index].connector, status },
  }
}

export const useConnectorsStore = create<ConnectorsState & ConnectorsActions>()(
  devtools(
    setStore => ({
      setConnectors: connectors => {
        setStore(
          produce(state => {
            state.connectors = connectors
          })
        )
      },
      connectors: [],
      updateStoreConnector: (id: string, updatedData: Partial<DataConnectorsType>) => {
        setStore(
          produce(state => {
            const index = state.connectors.findIndex((it: DataConnectorsType) => matchesConnectorId(it, id))
            if (index !== -1) {
              state.connectors[index] = {
                ...state.connectors[index],
                connector: { ...state.connectors[index].connector, ...updatedData.connector },
                config: { ...state.connectors[index].config, ...updatedData.config },
              }
            }
          })
        )
      },
      addStoreConnector: (newData: DataConnectorsType) => {
        setStore(
          produce(state => {
            const existingIndex = state.connectors.findIndex(
              (connector: DataConnectorsType) =>
                matchesConnectorId(connector, String(newData.connector?._id || newData._id || ''))
            );
            
            if (existingIndex !== -1) {
              state.connectors[existingIndex] = newData;
            } else {
              state.connectors.push(newData);
            }
          })
        )
      },
      deleteConnector: async (id: string) => {
        try {
          setStore(
            produce(state => {
              state.connectors = state.connectors.filter((it: DataConnectorsType) => !matchesConnectorId(it, id))
            })
          )
          const response = await deleteConnector(id, getAuthToken()!)
          logs(response)
        } catch (error) {
          console.error("Error:", error)
        }
      },
      updateConnectorById: async (newData: Partial<any>, id: string): Promise<DataConnectorsType | void> => {
        try {
          const { id: dataId, ...restData } = newData;
          return await updateConnector({ ...restData, id: id || dataId }, getAuthToken()!)
        } catch (error) {
          console.error("Error:", error)
        }
      },
      pauseConnectorById: async (id: string): Promise<void> => {
        setStore(produce(state => applyConnectorStatus(state, id, StatusEnum.PAUSE)))
        try {
          await pauseConnector(id, getAuthToken()!)
        } catch (error) {
          console.error("Error:", error)
        }
      },
      resumeConnectorById: async (id: string): Promise<void> => {
        setStore(produce(state => applyConnectorStatus(state, id, StatusEnum.ACTIVE)))
        try {
          await resumeConnector(id, getAuthToken()!)
        } catch (error) {
          console.error("Error:", error)
        }
      },
    }), {name: "connectors"}
  )
)
